import {
  FC,
  useState,
  useEffect,
  useLayoutEffect,
  ReactNode,
  Suspense,
} from 'react';
import { useOktaAuth } from '@okta/okta-react/';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation/Navigation';
import { Footer } from '../components/Footer/Footer';
import styled from 'styled-components';
import { AlertsDrawer } from '../components/Global/Alerts/AlertsDrawer';
import { useAlertsStore } from '../stores/alertsStore';
import { AlertsBanner } from '../components/Global/Alerts/AlertsBanner';
import { useAlertsOutagesSubscription } from '../utils/api/requests/useSubscriptions';
import {
  initializeBugsnag,
  setupGlobalErrorHandlers,
} from '../utils/BugSnag/BugSnagUtilities';
import { AlertInterface } from '../interfaces/AlertInterface';
import { useGetAnnouncements } from '../utils/api/requests/useQueries';
import { useVerifyActiveLocation } from '../hooks/useVerifyActiveLocation';
import {
  registerUser,
  updateAudienceAttribute,
  updateLocationTag,
} from '../utils/Airship/AirshipUtilities';
import { PageLoader as CorePageLoader } from '@cfa/react-core';
import { themeSizes } from '../styles/globalStyleVars';

interface Props {
  children: ReactNode;
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: auto;
`;

export const AppAuthWrapper: FC<Props> = ({ children }) => {
  const { authState } = useOktaAuth();
  const pathname = useLocation().pathname;

  useVerifyActiveLocation();

  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState<boolean>(false);
  const [alertChildCaseOpen, setAlertChildCaseOpen] = useState<
    AlertInterface | undefined
  >(undefined);

  // Do not add a tokenManager 'expired' handler here. The SDK's
  // AutoRenewService already owns expiry and dedupes concurrent renewals; a
  // handler here bypasses that and fires once per token, which signed users out
  // mid-edit. See SAW-10974.

  const {
    data: alertsAndOutages,
    error: alertsError,
    isFetching: alertsLoading,
    refetch: refetchAlerts,
  } = useGetAnnouncements(authState?.isAuthenticated);
  const { setAlertsOutages, setExpiredAlertsOutages, alertsOutages } =
    useAlertsStore();

  const showAlertBanner = ['/', '/forms'].includes(pathname);

  const bannerDisplayAlerts = alertsOutages
    .filter(alert => !alert.markDismissed)
    .slice(0, 2);

  useEffect(() => {
    if (!authState?.idToken) return;

    initializeBugsnag(authState.idToken.claims);
    setupGlobalErrorHandlers();

    //Airship
    registerUser(authState?.idToken?.claims['cfa-guid'].toString());
    updateAudienceAttribute(authState?.idToken?.claims?.cfa_aud.toString());
    authState.idToken.claims.userType !== 'Staff' &&
      authState.idToken.claims.userType !== 'Contractor' &&
      updateLocationTag(authState?.idToken?.claims['cfa-locations']);
  }, [authState?.isAuthenticated]);

  useLayoutEffect(() => {
    if (alertsAndOutages) {
      setAlertsOutages(alertsAndOutages.activeAnnouncements);
      setExpiredAlertsOutages(alertsAndOutages.expiredAnnouncements);
    }
  }, [alertsAndOutages]);

  useAlertsOutagesSubscription();

  return (
    <>
      {authState?.isAuthenticated ? (
        <PageWrapper>
          <AlertsDrawer
            alertsDrawerOpen={alertsDrawerOpen}
            setAlertsDrawerOpen={setAlertsDrawerOpen}
            setAlertChildCaseOpen={setAlertChildCaseOpen}
            alertChildCaseOpen={alertChildCaseOpen}
            alertsError={!!alertsError}
            alertsLoading={alertsLoading}
            refetchAlerts={refetchAlerts}
          />
          <Navigation
            alertsDrawerOpen={alertsDrawerOpen}
            setAlertsDrawerOpen={setAlertsDrawerOpen}
            alertsTotal={
              alertsAndOutages ? alertsAndOutages.activeAnnouncements.length : 0
            }
            alertsError={alertsError ? true : false}
          />
          {!alertsLoading &&
            !alertsError &&
            showAlertBanner &&
            bannerDisplayAlerts.length > 0 && (
              <AlertsBanner
                alertsAndOutages={bannerDisplayAlerts}
                setAlertsDrawerOpen={() => setAlertsDrawerOpen(true)}
                setAlertChildCaseOpen={setAlertChildCaseOpen}
              />
            )}
          <Suspense
            fallback={
              <CorePageLoader style={{ margin: `${themeSizes.xxl} auto` }} />
            }
          >
            {children}
          </Suspense>
          <Footer />
        </PageWrapper>
      ) : (
        <>{children}</>
      )}
    </>
  );
};
